import Backbone from 'backbone'
import _ from 'lodash'
import WizardProgress from './wizard-progress'
import template from './wizard.hbs'
import i18n from 'scoreboard/components/i18n'
import appDisplay from './app-display'

var Wizard = Backbone.View.extend({

	// this.options will record the last step of options passed in when calling
	// start, resume, next, or back.

	events: {
		'click .back': 'backClick',
		'click .cancel': 'cancel'
	},

	/**
	 * Constructs a new wizard
	 *
	 * @constructs
	 *
	 * @param {Array} options.views (required) the constructors for the views in the wizard
	 *
	 * @param {Number} [options.step=1] the current step to start the user on
	 *
	 * @param {Number} [options.lastNavigable=1] the last step the user can jump to
	 *
	 * @param {Function} [options.display] a callback used to place the view in the correct location
	 *
	 * @param {Boolean} [options.replaceDisplay] a callback used to transition from one view to another
	 * progress inside the dialog-footer element inside the provided element
	 *
	 * @param {String} [options.doneKey] function returning resource bundle key for the text of the primary button for the last step of the wizard
	 */
	initialize: function(options) {
		options = options || {}
		// keep track of stuff passed along when calling start, next, back, etc
		this.options = options
		this.views = options.views
		this.step = options.step || 1
		this.steps = options.steps || options.views.length
		this.lastNavigable = Math.max(this.step, options.lastNavigable || 1)
		this.display = options.display ||
			appDisplay.dialog.bind(appDisplay)
		this.replaceDisplay = options.replaceDisplay ||
			appDisplay.replaceDialog.bind(appDisplay)
	},

	/**
	 * Renders the dialog-footer for the wizard.  Note this should go before
	 * calls to formView.start so that the formView will have all the buttons
	 * present so it can listen to events properly.
	 *
	 * @param options
	 * @param options.el - (optional) the element requiring.  Not required if a formView is passed in
	 * @param options.formView (optional) if specified, the FormView to use for validation before switching steps.
	 * @param options.stateSaver function used to save the state of the wizard when switching steps
	 */
	render: function(options) {
		options = options || {}
		this.customPrimaryButtonKey = options.customPrimaryButtonKey
		this._extendOptions(options)
		this.steps = options.steps || this.steps
		if (options.el) {
			_.invokeMap([this._wizardProgress], 'remove')
			this.setElement(options.el)
		}
		if (options.formView) {
			// #19724 when a FormView is passed in, it needs to be used for
			// validation
			this.options.formView = options.formView
			if (!options.el) {
				_.invokeMap([this._wizardProgress], 'remove')
				this.setElement(options.formView.el)
			}
		}

		this.renderFooter()
		this._savePrevious()
	},

	renderFooter: function() {
		let standardPrimaryButtonKey = this.step < this.steps ? 'common.next' :
			this._doneKey()
		var primaryButtonKey = this.customPrimaryButtonKey ?
			this.customPrimaryButtonKey : standardPrimaryButtonKey
		// if this.el was specified, assume the primary button should be enabled
		var primaryButtonEnabled = !!this.options.el
		if (_.get(this.options.formView, 'options.allowCleanSubmission')) {
			primaryButtonEnabled = true
		} else if (_.get(this.options.formView, 'test')) {
			primaryButtonEnabled = !this.options.formView.test()
		}

		var footer = this.$('.dialog-footer')
		footer.html(template())
		footer.find('.button-primary')
			.text(i18n.get(primaryButtonKey))
			.prop('disabled', !primaryButtonEnabled)
		footer.find('.back').toggleClass('is-not-displayed', this.step == 1)
		footer.find('.wizard-progress').toggleClass('is-not-displayed', this.steps == 1)

		// Render and insert the wizard
		if (this.steps != 1) {
			// If the wizard-progress hasn't changed visibly, or if there's a step
			// before where we are, don't animate the wizard as it's being created.
			var animation = true
			if (this.previous && ((this.previous.step == this.step &&
				this.previous.steps == this.steps &&
				this.previous.lastNavigable == this.lastNavigable) ||
				this.previous.step != null)) {
				animation = false
			}

			// First, create the wizard. If there's no previousStep, that means we're
			// creating it for the first time, so we'll want an opening animation. If there
			// is a previousStep, we wont need an opening animation.
			var wizardProgress = this.wizardProgress({
				step: this.previous && this.previous.step || this.step,
				openingAnimation: animation,
				lastNavigable: this.lastNavigable
			}).render()
			footer.find('.wizard-progress-container').html(wizardProgress.el)

			// Next, re-enable wizard animations and have the wizard automatically
			// change itself to accurately display the current view's step/steps
			wizardProgress.toggleAnimation(true)
			wizardProgress.setValues({
				step: this.step,
				steps: this.steps,
				lastNavigable: this.lastNavigable
			})
		}

		// need to re-bind events
		if (this.options.formView) {
			this.options.formView.setElement(this.options.formView.el)
		}

		// #28544/#29119 need to rebind the submit buttons since wizard.js blows
		// away the previous buttons after each render.  Cannot use form.start
		// in order to retain any errors that might have occurred on the wizard
		if (_.get(this.options.formView, 'start')) {
			this.options.formView.setupButtons()
		}
	},

	_doneKey: function() {
		return _.result(this.options, 'doneKey', 'common.done')
	},

	/**
	 * Starts the wizard on the first step
	 * Must be invoked after the wizard is instantiated
	 *
	 * @param {Object} options - options that are used for this method, and also
	 * passed along to the View when it is created
	 *
	 * @param {jqXHR} [options.xhr] - if provided, will be passed along to dialog(),
	 * giving loading dots, rendering on XHR completion, etc. The results of
	 * the XHR will be passed to the View constructor options as 'xhrResponse'
	 */
	start: function(options) {
		this.xhr = _.result(options, 'xhr') || _.result(this.options, 'xhr')
		if (this.xhr) {
			this.display(this.xhr, function(xhrResponse) {
				return this._view(_.extend({ xhrResponse }, options))
			}.bind(this))
		} else {
			this.display(this._view(options))
		}
		return this
	},

	// resumes a previously completed wizard.  starts on the first step, but allow
	// navigating to any step right away.
	resume: function(options) {
		this.lastNavigable = this.views.length
		this.display(this._view(options))
	},

	/**
	 * Navigates to the next step in the wizard
	 * Must be invoked by each step (view component) when it is complete
	 *
	 * @param [options] The options to pass to the next backbone view
	 */
	next: function(options) {
		this.move(this.step+1, options)
		this.lastNavigable = Math.max(this.step, this.lastNavigable)
	},

	cancel: function() {
		this.trigger('cancel')
	},

	// the same as this.back, but don't mix jQuery event properties into our
	// this.options!
	backClick: function() {
		this.back()
	},

	// goes back to the previous step in the wizard
	back: function(options) {
		this.move(this.step-1, options)
	},

	// moves to the provided step
	move: function(step, options) {
		// validate the form when going forward steps, but not backward
		if (step > this.step) {
			var error = _.result(this.options.formView, 'validate')
			if (error) {
				return
			}
		}

		_.invoke(this, 'options.stateSaver')
		delete this.options.stateSaver
		delete this.options.formView

		// Save the previous step value as well so that it can be used in
		// wizard-progress stepping/animations
		this._savePrevious()

		this.step = step
		this.replaceDisplay(this._view(options))
	},

	// returns a WizardProgress view appropriate for the current state of the
	// wizard
	wizardProgress: function(opts) {
		this._wizardProgress = new WizardProgress(_.extend({
			step: this.step,
			steps: this.steps,
			lastNavigable: this.lastNavigable,
			openingAnimation: true
		}, opts))
		this._wizardProgress.on('jump', this.move.bind(this))
		return this._wizardProgress
	},

	setProgressValues: function (values) {
		this._wizardProgress.setValues(values)
		_.extend(this, _.pick(values, 'step', 'steps', 'lastNavigable'))
		this._savePrevious()
	},

	// creates the view for the current step
	_view: function(options) {
		this._extendOptions(options)
		// pass the wizard to the view
		this.step = _.get(options, 'step') || this.step
		var Constructor = this._viewConstructor(this.step)
		return new Constructor(this.options)
	},

	setViews: function(views) {
		this.views = views
		this.steps = _.size(views)
	},

	_savePrevious: function() {
		this.previous = {
			step: this.step,
			steps: this.steps,
			lastNavigable: this.lastNavigable
		}
	},

	// remembers the passed-in options.  important for navigating to
	// an arbitrary step
	_extendOptions: function(options) {
		_.extend(this.options, _.omit(options, 'step', 'xhr', 'formView', 'el'), { wizard: this })
	},

	/**
	 * Returns the constructor for the Backbone.View for the current step.
	 * Unlike _view, does not instantiated the view.
	 * @param step the step in the wizard, which is 1-based (so the first step
	 *     is 1)
	 */
	_viewConstructor: function(step) {
		return this.views[step - 1]
	},

	isLastStep: function() {
		return this.step == this.steps
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this._wizardProgress], 'remove')
	}

})

export default Wizard

Wizard.__filename__ = 'components/wizard.js'
